import { getMinMaxDate } from "app/pages/benchmark-tracker/helpers/helpers";
import { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import DurationService from "app/services/DurationService";

import { DEFAULT_TAB } from "../constants/common";
import { isValidTab } from "../helpers/isValidTab";
import type { BenchmarkUrlParams } from "../types/paramsTypes";

type IValidateBenchmarkUrlParams = {
    params: Omit<BenchmarkUrlParams, "key">;
    validCountryList: { id: number }[];
    validGranularity?: BenchmarkGranularity[];
};

const WEEKLY_LENGTH = 3;
const MONTHLY_LENGTH = 2;

const validateIsDurationCorrespondToGranularity = (duration: string, granularity: BenchmarkGranularity) => {
    const durationArray = duration.split("-")[0].split(".");

    if (granularity === BenchmarkGranularity.WEEKLY) {
        return durationArray.length === WEEKLY_LENGTH;
    }

    return durationArray.length === MONTHLY_LENGTH;
};

const MONTHLY_GRANULARITY_MONTHS_DIFF = 0;
const WEEKLY_GRANULARITY_DAYS_DIFF = 6;

interface IValidateDurationParams {
    duration: string;
    granularity: BenchmarkGranularity;
}

const validateDuration = ({ duration, granularity }: IValidateDurationParams) => {
    if (!validateIsDurationCorrespondToGranularity(duration, granularity)) {
        return false;
    }

    const { monthlyFrom, monthlyTo, weeklyFrom, weeklyTo } = getMinMaxDate();

    const {
        raw: { from, to },
    } = DurationService.getDurationData(duration);

    const startDuration = from.startOf("day").valueOf();
    const endDuration = to.startOf("day").valueOf();

    if (granularity === BenchmarkGranularity.MONTHLY) {
        const diff = DurationService.diffByUnit(from, to, "months");

        return (
            diff === MONTHLY_GRANULARITY_MONTHS_DIFF &&
            startDuration >= monthlyFrom.valueOf() &&
            endDuration <= monthlyTo.valueOf()
        );
    }

    // check that duration starts from Monday
    if (from.day() !== 1) {
        return false;
    }

    const diff = DurationService.diffByUnit(from, to, "days");

    if (diff !== WEEKLY_GRANULARITY_DAYS_DIFF) {
        return false;
    }

    return startDuration >= weeklyFrom.startOf("day").valueOf() && endDuration <= weeklyTo.endOf("day").valueOf();
};

export const validateBenchmarkUrlParams = ({
    params,
    validCountryList,
    validGranularity = [BenchmarkGranularity.WEEKLY, BenchmarkGranularity.MONTHLY],
}: IValidateBenchmarkUrlParams) => {
    const { country, granularity, webSource, duration, downloadPdf, tab } = params;

    const isTabValid = isValidTab(tab);
    const isCountryValid = validCountryList.some(({ id }) => id === Number(country));
    const isGranularityValid = validGranularity.includes(granularity);
    const isWebSourceValid = webSource === "Total";

    const isDurationValid = validateDuration({
        duration,
        granularity: isGranularityValid ? granularity : BenchmarkGranularity.WEEKLY,
    });

    const isValidDownloadPdf = downloadPdf == null || downloadPdf === "0" || downloadPdf === "1";

    const isAllParamsValid =
        isCountryValid && isDurationValid && isGranularityValid && isTabValid && isValidDownloadPdf && isWebSourceValid;

    return {
        isAllParamsValid,
        isCountryValid,
        isDurationValid,
        isGranularityValid,
        isTabValid,
        isValidDownloadPdf,
        isWebSourceValid,
    };
};
